import type { Question } from './questions';
import { questionChoices } from './questionChoices';

type Fact = [string, string];
const capitals: Fact[] = [
  ['法国', '巴黎'], ['德国', '柏林'], ['意大利', '罗马'], ['西班牙', '马德里'], ['葡萄牙', '里斯本'],
  ['希腊', '雅典'], ['奥地利', '维也纳'], ['匈牙利', '布达佩斯'], ['捷克', '布拉格'], ['波兰', '华沙'],
  ['瑞典', '斯德哥尔摩'], ['挪威', '奥斯陆'], ['芬兰', '赫尔辛基'], ['丹麦', '哥本哈根'], ['英国', '伦敦'],
  ['爱尔兰', '都柏林'], ['俄罗斯', '莫斯科'], ['日本', '东京'], ['韩国', '首尔'], ['泰国', '曼谷'],
  ['越南', '河内'], ['蒙古国', '乌兰巴托'], ['印度', '新德里'], ['尼泊尔', '加德满都'], ['埃及', '开罗'],
  ['肯尼亚', '内罗毕'], ['加拿大', '渥太华'], ['澳大利亚', '堪培拉'], ['新西兰', '惠灵顿'], ['巴西', '巴西利亚']
];
const elements: Fact[] = [
  ['H', '氢'], ['He', '氦'], ['Li', '锂'], ['Be', '铍'], ['B', '硼'], ['C', '碳'], ['N', '氮'], ['O', '氧'], ['F', '氟'], ['Ne', '氖'],
  ['Na', '钠'], ['Mg', '镁'], ['Al', '铝'], ['Si', '硅'], ['P', '磷'], ['S', '硫'], ['Cl', '氯'], ['Ar', '氩'], ['K', '钾'], ['Ca', '钙'],
  ['Fe', '铁'], ['Cu', '铜'], ['Zn', '锌'], ['Ag', '银'], ['Au', '金'], ['Hg', '汞'], ['Pb', '铅'], ['Sn', '锡'], ['W', '钨'], ['I', '碘']
];
const books: Fact[] = [
  ['呐喊', '鲁迅'], ['骆驼祥子', '老舍'], ['家', '巴金'], ['雷雨', '曹禺'], ['围城', '钱钟书'],
  ['边城', '沈从文'], ['活着', '余华'], ['我与地坛', '史铁生'], ['背影', '朱自清'], ['故都的秋', '郁达夫'],
  ['繁星', '冰心'], ['子夜', '茅盾'], ['西游记', '吴承恩'], ['三国演义', '罗贯中'], ['红楼梦', '曹雪芹'],
  ['春望', '杜甫'], ['山居秋暝', '王维'], ['赤壁赋', '苏轼'], ['醉翁亭记', '欧阳修'], ['小石潭记', '柳宗元']
];
const units: Fact[] = [
  ['力', '牛顿（N）'], ['压强', '帕斯卡（Pa）'], ['能量', '焦耳（J）'], ['功率', '瓦特（W）'], ['电荷量', '库仑（C）'],
  ['电势差', '伏特（V）'], ['电阻', '欧姆（Ω）'], ['电容', '法拉（F）'], ['电感', '亨利（H）'], ['频率', '赫兹（Hz）'],
  ['磁通量', '韦伯（Wb）'], ['磁感应强度', '特斯拉（T）'], ['电导', '西门子（S）'], ['放射性活度', '贝可勒尔（Bq）'],
  ['吸收剂量', '戈瑞（Gy）'], ['剂量当量', '希沃特（Sv）'], ['热力学温度', '开尔文（K）'], ['物质的量', '摩尔（mol）'],
  ['发光强度', '坎德拉（cd）'], ['长度', '米（m）']
];
const computing: Fact[] = [
  ['CPU', '执行指令并进行通用运算的中央处理器'], ['RAM', '供运行程序临时读写的随机存取存储器'], ['ROM', '主要用于保存固化内容的只读存储器'],
  ['SSD', '使用固态电子存储介质的存储设备'], ['GPU', '擅长图形处理及大量并行计算的处理器'], ['操作系统', '管理硬件资源并为应用提供基础服务的软件'],
  ['IP地址', '网络中标识和寻址网络接口的地址'], ['DNS', '把域名等名称解析到相应网络信息的系统'], ['HTTP', '用于传输网页等资源的应用层协议'],
  ['HTTPS', '通过TLS等机制保护的HTTP通信'], ['URL', '用于定位网络资源的地址表达'], ['HTML', '描述网页结构的标记语言'],
  ['CSS', '控制网页表现样式的样式表语言'], ['SQL', '查询和操作关系数据库的语言'], ['JSON', '表示和交换结构化数据的文本格式'],
  ['UTF-8', 'Unicode字符的一种可变长度编码方式'], ['ASCII', '包含基本拉丁字母等字符的编码标准'], ['PNG', '常用的无损栅格图像格式'],
  ['常见JPEG照片', '通常使用有损压缩的栅格图像文件'], ['SVG', '以矢量图形描述为主的图像格式'], ['ZIP', '可打包多个文件并支持压缩的归档格式'],
  ['PDF', '适合固定版式文档交换的文件格式'], ['CSV', '通常用分隔符组织表格数据的文本格式'], ['TCP', '提供可靠有序字节流的传输层协议'],
  ['UDP', '不保证送达与顺序的数据报传输协议'], ['LAN', '局域网'], ['WAN', '广域网'], ['GUI', '图形用户界面'],
  ['API', '软件组件之间约定的调用接口'], ['USB', '用于设备连接的通用串行总线']
];

const groups = [
  { id: 'capital', topic: '世界地理', facts: capitals, stem: (s: string) => s + '的首都是哪座城市？', explanation: (s: string, a: string) => s + '的首都是' + a + '。' },
  { id: 'element', topic: '化学元素', facts: elements, stem: (s: string) => '元素符号 ' + s + ' 对应哪种元素？', explanation: (s: string, a: string) => a + '的元素符号是' + s + '，注意字母的大小写。' },
  { id: 'book', topic: '文学作品', facts: books, stem: (s: string) => '《' + s + '》的作者是谁？', explanation: (s: string, a: string) => '《' + s + '》的作者是' + a + '。' },
  { id: 'unit', topic: '科学单位', facts: units, stem: (s: string) => s + '对应的国际单位制单位是？', explanation: (s: string, a: string) => s + '可用' + a + '表示。此题只辨认物理量与单位，不进行换算。' },
  { id: 'computing', topic: '计算机常识', facts: computing, stem: (s: string) => '关于 ' + s + '，哪项描述正确？', explanation: (s: string, a: string) => s + '：' + a + '。' }
];
export const GENERAL_KNOWLEDGE_QUESTIONS: Question[] = groups.flatMap(group => group.facts.map(([subject, correct], index) => {
  const id = 'knowledge-' + group.id + '-' + (index + 1), wrong: string[] = [];
  for (let n = 1; wrong.length < 3; n++) {
    const answer = group.facts[(index + n * 7) % group.facts.length][1];
    if (answer !== correct && !wrong.includes(answer)) wrong.push(answer);
  }
  return { id, category: 'culture', topic: group.topic, stem: group.stem(subject),
    explanation: group.explanation(subject, correct), ...questionChoices(id, correct, wrong) };
}));
